import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAccountTenantSubscription,
  fetchAccountTenants,
  fetchAccountTenantUsers,
  makeAccountTenantPrimary,
  fetchSubscriptionPlans,
  fetchTenantSubscription,
} from '@/api/promote';

export function useTenantSubscription(accountId?: string) {
  return useQuery({
    queryKey: ['crm', 'account', accountId ?? '', 'tenant-subscription'],
    queryFn: () => fetchTenantSubscription(accountId!),
    enabled: Boolean(accountId),
  });
}

export function useAccountTenants(accountId?: string) {
  return useQuery({
    queryKey: ['crm', 'account', accountId ?? '', 'tenants'],
    queryFn: () => fetchAccountTenants(accountId!),
    enabled: Boolean(accountId),
  });
}

export function useAccountTenantSubscription(accountId?: string, tenantId?: string) {
  return useQuery({
    queryKey: ['crm', 'account', accountId ?? '', 'tenants', tenantId ?? '', 'subscription'],
    queryFn: () => fetchAccountTenantSubscription(accountId!, tenantId!),
    enabled: Boolean(accountId && tenantId),
  });
}

export function useAccountTenantUsers(accountId?: string, enabled = true) {
  return useQuery({
    queryKey: ['crm', 'account', accountId ?? '', 'tenant-users'],
    queryFn: () => fetchAccountTenantUsers(accountId!),
    enabled: Boolean(accountId) && enabled,
  });
}

export function useMakeAccountTenantPrimary(accountId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) => makeAccountTenantPrimary(accountId, tenantId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm', 'account', accountId] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'account', accountId, 'tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'accounts'] }),
      ]);
    },
  });
}

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscriptions', 'plans'],
    queryFn: fetchSubscriptionPlans,
  });
}
